// /frontend/src/pages/Settings.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import IntegrationsSection from "../components/IntegrationsSection";

import { formatPhoneE164 } from "../utils/formatters";
import {
  User2,
  Lock,
  Bell,
  Palette,
  ChevronRight,
  Globe,
  Plug,
  CheckCircle2,
  RefreshCw,
  Bot,
  Shield,
  Smartphone,
  Key,
  AlertTriangle,
  Copy,
  Check,
  Trash2,
  ShieldCheck,
  Save,
  Upload,
  UploadCloud,
  Phone,
  Loader2
} from "lucide-react";
import { updateAssistantName } from "../api";
import TwoFactorSetup from "../components/TwoFactorSetup";
import PasswordStrengthMeter, { validatePasswordStrength } from "../components/PasswordStrengthMeter";
import NotificationSettings from "../components/NotificationSettings";

const API_BASE = process.env.REACT_APP_API_URL || '';

// Build sidebar options based on user role
const getSidebarOptions = (t, userRole) => {
  const baseOptions = [
    { key: "profile", label: t('profileLogin'), icon: <User2 size={18} /> },
    { key: "phone", label: "Phone System", icon: <Phone size={18} /> },
    { key: "security", label: t('security'), icon: <Lock size={18} /> },
    { key: "language", label: t('languagePreference'), icon: <Globe size={18} /> },
    { key: "notifications", label: t('notifications'), icon: <Bell size={18} /> },
    { key: "integrations", label: t('integrations'), icon: <Plug size={18} /> },
    { key: "branding", label: t('brandingTheme'), icon: <Palette size={18} /> },
  ];

  // Only add danger zone for all users
  const dangerOption = { key: "danger", label: t('dangerZone'), icon: <Trash2 size={18} /> };

  return [...baseOptions, dangerOption];
};

// Personal Phone Section Component for Call Forwarding
const PersonalPhoneSection = () => {
  const [personalPhone, setPersonalPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load current personal phone on mount
  useEffect(() => {
    const loadPersonalPhone = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/profile`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.user?.personal_phone) {
          setPersonalPhone(data.user.personal_phone);
        }
      } catch (err) {
        console.error('Failed to load personal phone:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPersonalPhone();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Format phone to E164
      let formattedPhone = personalPhone.replace(/\D/g, '');

      // Validate phone number length (if not empty)
      if (formattedPhone && formattedPhone.length !== 10 && formattedPhone.length !== 11) {
        setError('Please enter a valid 10-digit phone number');
        setSaving(false);
        return;
      }

      if (formattedPhone.length === 10) {
        formattedPhone = '+1' + formattedPhone;
      } else if (formattedPhone.length === 11 && formattedPhone.startsWith('1')) {
        formattedPhone = '+' + formattedPhone;
      }

      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ personal_phone: formattedPhone || null })
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        setError(data.error || 'Failed to save. Please try again.');
      }
    } catch (err) {
      console.error('Failed to save personal phone:', err);
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
      <h3 className="font-semibold text-[#172A3A] mb-2">Call Forwarding</h3>
      <p className="text-sm text-slate-500 mb-4">
        When clients call your business number, the call will be forwarded to this phone.
        The caller ID will show the client's number, not your Twilio number.
      </p>

      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Your Personal Phone Number
          </label>
          <input
            type="tel"
            value={personalPhone}
            onChange={(e) => setPersonalPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`self-end px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A]'
          }`}
        >
          {saving ? (
            <Loader2 size={16} className="animate-spin" />
          ) : saved ? (
            <>
              <Check size={16} />
              Saved
            </>
          ) : (
            <>
              <Save size={16} />
              Save
            </>
          )}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-500 mt-2">{error}</p>
      )}

      <p className="text-xs text-slate-400 mt-2">
        Leave blank to send calls to voicemail instead.
      </p>
    </div>
  );
};

// Voicemail Greeting Section Component
const VoicemailGreetingSection = () => {
  const [greeting, setGreeting] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCustom, setIsCustom] = useState(false);

  const defaultGreeting = "Hello, you've reached [Your Name]. I'm currently unavailable. Please leave a message after the tone and I'll get back to you as soon as possible.";

  // Load current voicemail greeting on mount
  useEffect(() => {
    const loadGreeting = async () => {
      try {
        const res = await fetch(`${API_BASE}/calls/voicemail-greeting`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (data.ok && data.data) {
          setGreeting(data.data.greeting || '');
          setIsCustom(data.data.isCustom || false);
        }
      } catch (err) {
        console.error('Failed to load voicemail greeting:', err);
      } finally {
        setLoading(false);
      }
    };
    loadGreeting();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/calls/voicemail-greeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ greeting: greeting.trim() || null })
      });

      if (res.ok) {
        const data = await res.json();
        setIsCustom(!!greeting.trim());
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (err) {
      console.error('Failed to save voicemail greeting:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setGreeting('');
    setSaving(true);
    try {
      await fetch(`${API_BASE}/calls/voicemail-greeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ greeting: null })
      });
      setIsCustom(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to reset voicemail greeting:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-4 w-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
      <h3 className="font-semibold text-[#172A3A] mb-2">Voicemail Greeting</h3>
      <p className="text-sm text-slate-500 mb-4">
        Customize the message callers hear before leaving a voicemail.
        This will be read aloud using text-to-speech.
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Your Greeting Message
        </label>
        <textarea
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          placeholder={defaultGreeting}
          rows={4}
          maxLength={500}
          className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
        />
        <div className="flex justify-between mt-1">
          <p className="text-xs text-slate-400">
            {isCustom ? 'Using custom greeting' : 'Using default greeting (your name will be inserted automatically)'}
          </p>
          <p className="text-xs text-slate-400">{greeting.length}/500</p>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-4 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A]'
          }`}
        >
          {saving ? (
            <Loader2 size={16} className="animate-spin" />
          ) : saved ? (
            <>
              <Check size={16} />
              Saved
            </>
          ) : (
            <>
              <Save size={16} />
              Save Greeting
            </>
          )}
        </button>

        {isCustom && (
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Reset to Default
          </button>
        )}
      </div>
    </div>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n, t } = useTranslation();
  const initialSection = searchParams.get('section') || 'profile';
  const [section, setSection] = useState(initialSection);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('agent');

  // Branding state
  const [brandingSettings, setBrandingSettings] = useState({
    primaryColor: '#FFB800',
    logoUrl: '',
    companyName: '',
    emailSignature: ''
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingSaved, setBrandingSaved] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [extractedColors, setExtractedColors] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [accentColor, setAccentColor] = useState('#0B1120');
  const [agencyBrandingInfo, setAgencyBrandingInfo] = useState(null);
  const [allowCustomBranding, setAllowCustomBranding] = useState(true);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [languageSaved, setLanguageSaved] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  const [assistantNameInput, setAssistantNameInput] = useState('');
  const [savingAssistant, setSavingAssistant] = useState(false);
  const [assistantSaved, setAssistantSaved] = useState(false);

  // 2FA State
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loadingMfa, setLoadingMfa] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [regeneratingCodes, setRegeneratingCodes] = useState(false);
  const [disablingMfa, setDisablingMfa] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerateError, setRegenerateError] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Phone/Twilio status state
  const [twilioStatus, setTwilioStatus] = useState(null);
  const [loadingTwilio, setLoadingTwilio] = useState(false);
  const [retryingTwilio, setRetryingTwilio] = useState(false);
  const [twilioError, setTwilioError] = useState('');

  useEffect(() => {
    async function loadProfile() {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          const json = await res.json();
          const userData = {
            name: json.user.name || "",
            email: json.user.email || "",
            role: json.user.role || "agent",
            phone: json.user.phone || ""
          };
          setProfile(userData);
          setUserRole(json.user.role || 'agent');
          setProfileForm({ name: userData.name, phone: userData.phone });
          setAssistantNameInput(json.user.assistantName || 'Alex');

          // Load branding settings if available
          if (json.user.branding) {
            setBrandingSettings(json.user.branding);
          }
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
      } finally {
        setLoading(false);
      }
    }
    loadProfile();
  }, []);

  // Save profile changes
  const saveProfile = async () => {
    setSavingProfile(true);
    setProfileError('');

    try {
      const formattedPhone = profileForm.phone ? formatPhoneE164(profileForm.phone) : '';
      const res = await fetch(`${API_BASE}/auth/update-profile`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: profileForm.name,
          phone: formattedPhone
        })
      });

      if (res.ok) {
        setProfile(prev => ({
          ...prev,
          name: profileForm.name,
          phone: formattedPhone
        }));
        setProfileSaved(true);
        setEditingProfile(false);
        setTimeout(() => setProfileSaved(false), 3000);
      } else {
        const json = await res.json();
        setProfileError(json.error || 'Failed to save profile');
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setProfileError('Failed to save profile. Please try again.');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveLanguagePreference = () => {
    i18n.changeLanguage(selectedLanguage);
    setLanguageSaved(true);
    setTimeout(() => setLanguageSaved(false), 3000);
  };

  const saveAssistantName = async () => {
    setSavingAssistant(true);
    try {
      await updateAssistantName(assistantNameInput);
      setAssistantSaved(true);
      setTimeout(() => setAssistantSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save assistant name:', err);
      alert('Failed to save assistant name. Please try again.');
    } finally {
      setSavingAssistant(false);
    }
  };

  // Handle logo file selection
  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);

    // Auto-upload and extract colors
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await fetch(`${API_BASE}/api/branding/logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      if (res.ok) {
        const json = await res.json();
        setExtractedColors(json.extractedColors || []);
        setBrandingSettings(prev => ({ ...prev, logoUrl: json.logoUrl }));
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Save branding settings
  const saveBrandingSettings = async () => {
    setSavingBranding(true);
    try {
      await Promise.all([
        fetch(`${API_BASE}/api/branding/colors`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ primaryColor: brandingSettings.primaryColor, accentColor })
        }),
        fetch(`${API_BASE}/api/branding/settings`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyName: brandingSettings.companyName, emailSignature: brandingSettings.emailSignature })
        })
      ]);
      setBrandingSaved(true);
      setTimeout(() => setBrandingSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save branding:', err);
      alert('Failed to save branding settings.');
    } finally {
      setSavingBranding(false);
    }
  };

  // Load integrations when viewing integrations section
  useEffect(() => {
    if (section === 'integrations') {
      loadIntegrations();
    }
  }, [section]);

  // Load agency branding when viewing branding section
  useEffect(() => {
    if (section === 'branding') {
      fetch(`${API_BASE}/api/branding/agency`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : {})
        .then(j => {
          setAllowCustomBranding(j.allowCustom !== false);
          setAgencyBrandingInfo(j.agencyBranding || null);
        })
        .catch(() => {});
    }
  }, [section]);

  // Load MFA status when viewing security section
  useEffect(() => {
    if (section === 'security') {
      loadMfaStatus();
    }
  }, [section]);

  const loadMfaStatus = async () => {
    setLoadingMfa(true);
    try {
      const res = await fetch(`${API_BASE}/mfa/status`, {
        credentials: 'include',
      });
      if (res.ok) {
        const json = await res.json();
        setMfaStatus(json);
      }
    } catch (err) {
      console.error("Failed to load MFA status:", err);
    } finally {
      setLoadingMfa(false);
    }
  };

  const handleMfaSetupComplete = () => {
    setShowMfaSetup(false);
    loadMfaStatus();
  };

  const regenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length !== 6) {
      setRegenerateError('Please enter a valid 6-digit code');
      return;
    }

    setRegeneratingCodes(true);
    setRegenerateError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/regenerate-backup-codes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentCode: regenerateCode })
      });

      if (res.ok) {
        const json = await res.json();
        setBackupCodes(json.backupCodes || []);
        setShowBackupCodes(true);
        setShowRegenerateConfirm(false);
        setRegenerateCode('');
      } else {
        const errJson = await res.json();
        setRegenerateError(errJson.error || 'Failed to regenerate backup codes');
      }
    } catch (err) {
      console.error('Failed to regenerate backup codes:', err);
      setRegenerateError('Failed to regenerate backup codes. Please try again.');
    } finally {
      setRegeneratingCodes(false);
    }
  };

  const disableMfa = async () => {
    if (!disablePassword || !disableCode) {
      setDisableError('Please enter your password and 2FA code');
      return;
    }

    setDisablingMfa(true);
    setDisableError('');

    try {
      const res = await fetch(`${API_BASE}/mfa/disable`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: disablePassword,
          code: disableCode
        })
      });

      if (res.ok) {
        setShowDisableConfirm(false);
        setDisablePassword('');
        setDisableCode('');
        loadMfaStatus();
        alert('Two-factor authentication has been disabled');
      } else {
        const errJson = await res.json();
        setDisableError(errJson.error || 'Failed to disable 2FA');
      }
    } catch (err) {
      console.error('Failed to disable MFA:', err);
      setDisableError('Failed to disable 2FA. Please try again.');
    } finally {
      setDisablingMfa(false);
    }
  };

  const copyBackupCodes = async () => {
    const codesText = backupCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setBackupCodesCopied(true);
    setTimeout(() => setBackupCodesCopied(false), 2000);
  };

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields');
      return;
    }

    const passwordStrengthError = validatePasswordStrength(newPassword, profile?.email);
    if (passwordStrengthError) {
      setPasswordError(passwordStrengthError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    setPasswordError('');

    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (res.ok) {
        setPasswordSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(false), 3000);
      } else {
        const errJson = await res.json();
        setPasswordError(errJson.error || 'Failed to change password');
      }
    } catch (err) {
      console.error('Failed to change password:', err);
      setPasswordError('Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const loadIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const res = await fetch(`${API_BASE}/calendar/integrations`, {
        credentials: 'include',
      });
      if (res.ok) {
        const json = await res.json();
        setIntegrations(json.integrations || []);
      }
    } catch (err) {
      console.error("Failed to load integrations:", err);
    } finally {
      setLoadingIntegrations(false);
    }
  };

  // Load Twilio status when viewing phone section
  useEffect(() => {
    if (section === 'phone') {
      loadTwilioStatus();
    }
  }, [section]);

  const loadTwilioStatus = async () => {
    setLoadingTwilio(true);
    setTwilioError('');
    try {
      console.log('[Settings] Loading Twilio status...');
      const res = await fetch(`${API_BASE}/onboarding/twilio-status`, {
        credentials: 'include',
      });
      if (res.ok) {
        const json = await res.json();
        console.log('[Settings] Twilio status response:', json);
        console.log('[Settings] twilioStatus.provisioned:', json.twilioStatus?.provisioned);
        console.log('[Settings] twilioStatus.phoneNumber:', json.twilioStatus?.phoneNumber);
        setTwilioStatus(json.twilioStatus);
      }
    } catch (err) {
      console.error("[Settings] Failed to load Twilio status:", err);
      setTwilioError('Failed to load phone system status');
    } finally {
      setLoadingTwilio(false);
    }
  };

  const retryTwilioProvisioning = async () => {
    console.log('[Settings] Start Phone Setup clicked');
    setRetryingTwilio(true);
    setTwilioError('');
    try {
      console.log('[Settings] Calling /onboarding/twilio/retry...');
      const res = await fetch(`${API_BASE}/onboarding/twilio/retry`, {
        method: 'POST',
        credentials: 'include',
      });
      const json = await res.json();
      console.log('[Settings] Response:', json);
      if (!res.ok) throw new Error(json.error);
      loadTwilioStatus();
    } catch (err) {
      console.error("[Settings] Failed to retry Twilio provisioning:", err);
      setTwilioError(err.message || 'Failed to retry phone setup');
    } finally {
      setRetryingTwilio(false);
    }
  };

  const connectIntegration = async (provider) => {
    try {
      const res = await fetch(`${API_BASE}/calendar/${provider}/connect`, {
        credentials: 'include',
      });
      if (res.ok) {
        const json = await res.json();
        if (json.authUrl) {
          window.location.href = json.authUrl;
        }
      }
    } catch (err) {
      console.error(`Failed to connect ${provider}:`, err);
      alert(`Failed to connect ${provider}. Please try again.`);
    }
  };

  const disconnectIntegration = async (provider) => {
    if (!confirm(`Are you sure you want to disconnect ${provider === 'google' ? 'Google' : 'Microsoft'}? This will stop email sending and calendar sync.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/calendar/integrations/${provider}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        await loadIntegrations();
        alert(`${provider === 'google' ? 'Google' : 'Microsoft'} disconnected successfully!`);
      }
    } catch (err) {
      console.error(`Failed to disconnect ${provider}:`, err);
      alert(`Failed to disconnect ${provider}. Please try again.`);
    }
  };

  // Mobile section selector state
  const [showMobileSections, setShowMobileSections] = useState(false);

  const sidebarOptions = getSidebarOptions(t, userRole);

  return (
    <div className="flex flex-col lg:flex-row max-w-6xl mx-auto bg-[#f6f7fb] min-h-[80vh] lg:rounded-2xl shadow-lg border">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b p-4">
        <div className="flex items-center justify-between">
          <div className="text-xl font-extrabold text-[#172A3A]">{t('settings')}</div>
          <button
            onClick={() => setShowMobileSections(!showMobileSections)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium"
          >
            {sidebarOptions.find(o => o.key === section)?.icon}
            <span className="max-w-[120px] truncate">{sidebarOptions.find(o => o.key === section)?.label}</span>
            <ChevronRight size={16} className={`transition ${showMobileSections ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {/* Mobile Section Picker */}
        {showMobileSections && (
          <div className="mt-3 bg-white rounded-xl shadow-lg border overflow-hidden max-h-80 overflow-y-auto">
            {sidebarOptions.map(opt => (
              <button
                key={opt.key}
                className={`flex items-center w-full gap-3 px-4 py-3 text-left border-b border-gray-100 last:border-0 ${
                  section === opt.key
                    ? "bg-[#FFB800]/10 text-[#172A3A] font-semibold"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => { setSection(opt.key); setShowMobileSections(false); }}
              >
                {opt.icon}
                <span>{opt.label}</span>
                {section === opt.key && <CheckCircle2 size={16} className="ml-auto text-[#FFB800]" />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop Sidebar - hidden on mobile */}
      <div className="hidden lg:flex min-w-[230px] border-r bg-white rounded-l-2xl flex-col py-6 px-3">
        <div className="text-2xl font-extrabold text-[#172A3A] px-2 pb-6 tracking-tight">
          {t('settings')}
        </div>
        <ul className="flex-1">
          {sidebarOptions.map(opt => (
            <li key={opt.key}>
              <button
                data-tour={`settings-sidebar-${opt.key}`}
                className={`flex items-center w-full gap-2 px-4 py-3 rounded-lg mb-1 font-medium text-left transition ${
                  section === opt.key
                    ? "bg-[#FFB800]/20 text-[#172A3A] font-bold"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
                onClick={() => setSection(opt.key)}
              >
                {opt.icon}
                <span>{opt.label}</span>
                {section === opt.key && (
                  <ChevronRight size={18} className="ml-auto text-[#FFB800]" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 lg:p-12 bg-[#f6f7fb] lg:rounded-r-2xl min-h-[500px] lg:min-h-[700px]">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading...</div>
          </div>
        ) : (
          <>
        {/* Profile Section */}
        {section === "profile" && (
          <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl" data-tour="settings-profile">
            <div className="flex items-center justify-between mb-4" data-tour="settings-profile-header">
              <div className="text-lg sm:text-xl font-bold text-[#172A3A]">{t('profileLogin')}</div>
              {!editingProfile && (
                <button
                  onClick={() => setEditingProfile(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  Edit Profile
                </button>
              )}
            </div>

            {profileSaved && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <Check size={16} /> Profile saved successfully!
              </div>
            )}

            {profileError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {profileError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="font-semibold text-[#172A3A] block mb-1">{t('name')}</label>
                {editingProfile ? (
                  <input
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your full name"
                  />
                ) : (
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                    value={profile?.name || "Not set"}
                    readOnly
                  />
                )}
              </div>
              <div>
                <label className="font-semibold text-[#172A3A] block mb-1">{t('email')}</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                  value={profile?.email || ""}
                  type="email"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed. Contact support if needed.</p>
              </div>
              <div>
                <label className="font-semibold text-[#172A3A] block mb-1">{t('role')}</label>
                <input
                  className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                  value={profile?.role || ""}
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Role is assigned by administrators.</p>
              </div>
              <div>
                <label className="font-semibold text-[#172A3A] block mb-1">
                  {t('phoneForCall') || 'Phone Number'}
                </label>
                {editingProfile ? (
                  <input
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                  />
                ) : (
                  <input
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                    value={profile?.phone || "Not set"}
                    readOnly
                  />
                )}
                <p className="text-sm text-gray-600 mt-1">
                  {t('phoneWillRing') || 'Calls will forward to this number. For more options, see Phone System settings.'}
                </p>
              </div>
            </div>

            {editingProfile && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-6">
                <button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] px-6 py-3 sm:py-2 font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditingProfile(false);
                    setProfileForm({ name: profile?.name || '', phone: profile?.phone || '' });
                    setProfileError('');
                  }}
                  className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 sm:py-2 font-medium rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* AI Assistant Name Section */}
            <div className="mt-8 pt-6 border-t" data-tour="settings-ai-assistant">
              <div className="flex items-center gap-3 mb-4">
                <Bot className="text-blue-600" size={24} />
                <div className="text-xl font-bold text-[#172A3A]">AI Assistant</div>
              </div>
              <p className="text-gray-600 mb-4">
                Name your AI assistant! This name will appear in your daily briefings when your assistant gives you updates about your clients and tasks.
              </p>
              <div className="mb-4">
                <label className="font-semibold text-[#172A3A] block mb-1">
                  Assistant Name
                </label>
                <input
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                  value={assistantNameInput}
                  onChange={(e) => setAssistantNameInput(e.target.value)}
                  type="text"
                  placeholder="Alex"
                  maxLength={50}
                />
                <p className="text-sm text-gray-600 mt-1">
                  Your assistant will introduce themselves like: "Your morning brief from <b>{assistantNameInput || 'Alex'}</b>, your AI assistant"
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <button
                  onClick={saveAssistantName}
                  disabled={savingAssistant || !assistantNameInput.trim()}
                  className="w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] px-6 py-3 sm:py-2 font-bold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingAssistant ? 'Saving...' : 'Save Assistant Name'}
                </button>
                {assistantSaved && (
                  <div className="text-green-600 font-semibold flex items-center gap-2">
                    <Check size={16} /> Assistant name saved!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Phone System */}
        {section === "phone" && (
          <div className="space-y-6" data-tour="settings-phone">
            <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <Phone className="text-blue-600" size={24} />
                <div className="text-xl font-bold text-[#172A3A]">Phone System</div>
              </div>

              {loadingTwilio ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : twilioStatus?.provisioned ? (
                <div>
                  {/* Phone Number Display */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl mb-6">
                    <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                      <CheckCircle2 size={18} />
                      Phone System Active
                    </div>
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500 mb-1">Your Business Phone Number</p>
                      <p className="text-3xl font-bold text-[#172A3A]">{twilioStatus.phoneNumber}</p>
                    </div>
                  </div>

                  {/* A2P Status */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-[#172A3A] mb-3">A2P SMS Registration</h3>
                    <div className={`p-4 rounded-xl border ${
                      twilioStatus.a2pStatus === 'approved'
                        ? 'bg-green-50 border-green-200'
                        : twilioStatus.a2pStatus === 'pending'
                        ? 'bg-yellow-50 border-yellow-200'
                        : twilioStatus.a2pStatus === 'rejected'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          twilioStatus.a2pStatus === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : twilioStatus.a2pStatus === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : twilioStatus.a2pStatus === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {twilioStatus.a2pStatus === 'approved' ? 'Approved' :
                           twilioStatus.a2pStatus === 'pending' ? 'Pending Review' :
                           twilioStatus.a2pStatus === 'rejected' ? 'Rejected' :
                           twilioStatus.a2pStatus || 'Not Started'}
                        </span>
                      </div>

                      {twilioStatus.a2pStatus === 'pending' && (
                        <p className="text-sm text-yellow-700 mt-3">
                          Your A2P registration is being reviewed. This typically takes 24-48 hours for sole proprietors
                          or 1-2 weeks for businesses. You can send SMS with limited throughput during this time.
                        </p>
                      )}

                      {twilioStatus.a2pStatus === 'approved' && (
                        <p className="text-sm text-green-700 mt-3">
                          Your A2P registration is approved. You have full SMS throughput capabilities.
                        </p>
                      )}

                      {twilioStatus.a2pStatus === 'rejected' && twilioStatus.a2pRejectionReason && (
                        <div className="mt-3">
                          <p className="text-sm text-red-700 font-medium">Rejection Reason:</p>
                          <p className="text-sm text-red-600 mt-1">{twilioStatus.a2pRejectionReason}</p>
                          <p className="text-sm text-slate-600 mt-2">
                            Please contact support to resolve this issue and resubmit your registration.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Business Info Summary */}
                  {twilioStatus.businessName && (
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                      <h3 className="font-semibold text-[#172A3A] mb-3">Registered Business Info</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Name</span>
                          <span className="font-medium text-slate-700">{twilioStatus.businessName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Type</span>
                          <span className="font-medium text-slate-700 capitalize">
                            {twilioStatus.entityType === 'sole_proprietor' ? 'Sole Proprietor' : 'Business'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : twilioStatus?.status === 'failed' ? (
                <div>
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl mb-6">
                    <div className="flex items-center gap-2 text-red-700 font-semibold">
                      <AlertTriangle size={18} />
                      Setup Failed
                    </div>
                    {twilioStatus.error && (
                      <p className="text-sm text-red-600 mt-2">{twilioStatus.error}</p>
                    )}
                  </div>

                  {twilioError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {twilioError}
                    </div>
                  )}

                  <button
                    onClick={retryTwilioProvisioning}
                    disabled={retryingTwilio}
                    className="bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold px-6 py-3 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {retryingTwilio ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        Retry Setup
                      </>
                    )}
                  </button>
                </div>
              ) : twilioStatus?.status === 'in_progress' ? (
                <div className="text-center py-8">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
                  <p className="font-semibold text-slate-700">Setting up your phone system...</p>
                  <p className="text-sm text-slate-500 mt-1">This usually takes 30-60 seconds</p>
                </div>
              ) : (
                <div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                    <div className="flex items-center gap-2 text-amber-700 font-semibold">
                      <AlertTriangle size={18} />
                      Phone System Not Set Up
                    </div>
                    <p className="text-sm text-amber-600 mt-2">
                      Set up your dedicated business phone number to send SMS and make calls.
                    </p>
                  </div>

                  {twilioError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {twilioError}
                    </div>
                  )}

                  <button
                    onClick={retryTwilioProvisioning}
                    disabled={retryingTwilio}
                    className="bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold px-6 py-3 rounded-lg transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {retryingTwilio ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        <Phone size={18} />
                        Start Phone Setup
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Personal Phone for Call Forwarding */}
            {twilioStatus?.provisioned && (
              <PersonalPhoneSection />
            )}

            {/* Voicemail Greeting */}
            {twilioStatus?.provisioned && (
              <VoicemailGreetingSection />
            )}

            {/* Phone Blast - Announce New Number to Clients */}
            {twilioStatus?.provisioned && (
              <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-[#172A3A]">New Phone Number Blast</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Announce your new work phone number to all clients
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/phone-blast')}
                    className="bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-semibold px-4 py-2 rounded-lg transition flex items-center gap-2"
                  >
                    <Phone size={18} />
                    Open
                  </button>
                </div>
              </div>
            )}

            {/* Phone Features Info */}
            <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
              <h3 className="font-semibold text-[#172A3A] mb-4">Phone System Features</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">Dedicated Business Number</p>
                    <p className="text-sm text-slate-500">Your own phone number for calls and SMS</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">A2P 10DLC Compliant</p>
                    <p className="text-sm text-slate-500">FCC compliant SMS delivery for better deliverability</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">Call Forwarding</p>
                    <p className="text-sm text-slate-500">Calls route to your personal phone</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-700">SMS Automation</p>
                    <p className="text-sm text-slate-500">Send appointment reminders and check-ins</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security */}
        {section === "security" && (
          <div className="space-y-6" data-tour="settings-security">
            {/* Show 2FA Setup Modal */}
            {showMfaSetup && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <TwoFactorSetup
                  onComplete={handleMfaSetupComplete}
                  onCancel={() => setShowMfaSetup(false)}
                />
              </div>
            )}

            {/* Show Backup Codes Modal */}
            {showBackupCodes && backupCodes.length > 0 && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 max-w-lg w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <Key className="text-amber-500" size={24} />
                    <h2 className="text-xl font-bold text-[#172A3A]">New Backup Codes</h2>
                  </div>

                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <p className="text-sm text-amber-700">
                      <strong>Important:</strong> Store these codes in a safe place. Each code can only be used once.
                      <strong className="text-red-600"> These will only be shown once!</strong>
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="font-mono text-sm bg-gray-100 px-3 py-2 rounded border text-center">
                        {code}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={copyBackupCodes}
                    className="w-full mb-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                  >
                    {backupCodesCopied ? (
                      <><Check size={16} className="text-green-600" /> Copied!</>
                    ) : (
                      <><Copy size={16} /> Copy All Codes</>
                    )}
                  </button>

                  <button
                    onClick={() => { setShowBackupCodes(false); setBackupCodes([]); }}
                    className="w-full bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-3 rounded-lg"
                  >
                    I've Saved My Codes
                  </button>
                </div>
              </div>
            )}

            {/* Show Regenerate Backup Codes Confirmation */}
            {showRegenerateConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <Key className="text-amber-500" size={24} />
                    <h2 className="text-xl font-bold text-[#172A3A]">Regenerate Backup Codes</h2>
                  </div>

                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                    <p className="text-sm text-amber-700">
                      <strong>Warning:</strong> This will invalidate all your existing backup codes.
                      Make sure you save the new codes after regenerating.
                    </p>
                  </div>

                  {regenerateError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {regenerateError}
                    </div>
                  )}

                  <div className="mb-6">
                    <label className="font-semibold text-[#172A3A] block mb-1">Enter 2FA Code</label>
                    <input
                      type="text"
                      value={regenerateCode}
                      onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] font-mono text-center tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the code from your authenticator app</p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowRegenerateConfirm(false); setRegenerateCode(''); setRegenerateError(''); }}
                      className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={regenerateBackupCodes}
                      disabled={regeneratingCodes || regenerateCode.length !== 6}
                      className="flex-1 bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold py-2 rounded-lg disabled:opacity-50"
                    >
                      {regeneratingCodes ? 'Generating...' : 'Generate New Codes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Show Disable 2FA Confirmation */}
            {showDisableConfirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-lg p-6 max-w-md w-full">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="text-red-500" size={24} />
                    <h2 className="text-xl font-bold text-[#172A3A]">Disable 2FA</h2>
                  </div>

                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Warning:</strong> Disabling two-factor authentication will make your account less secure.
                    </p>
                  </div>

                  {disableError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {disableError}
                    </div>
                  )}

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="font-semibold text-[#172A3A] block mb-1">Password</label>
                      <input
                        type="password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800]"
                        placeholder="Enter your password"
                      />
                    </div>
                    <div>
                      <label className="font-semibold text-[#172A3A] block mb-1">2FA Code</label>
                      <input
                        type="text"
                        value={disableCode}
                        onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] font-mono text-center tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowDisableConfirm(false); setDisablePassword(''); setDisableCode(''); setDisableError(''); }}
                      className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={disableMfa}
                      disabled={disablingMfa}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 rounded-lg disabled:opacity-50"
                    >
                      {disablingMfa ? 'Disabling...' : 'Disable 2FA'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Password Change Section */}
            <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="text-blue-600" size={24} />
                <div className="text-xl font-bold text-[#172A3A]">Change Password</div>
              </div>

              {passwordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                  <Check size={16} /> Password changed successfully!
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="font-semibold text-[#172A3A] block mb-1">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="font-semibold text-[#172A3A] block mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                    placeholder="Create a strong password"
                  />
                  <PasswordStrengthMeter
                    password={newPassword}
                    email={profile?.email}
                    showRequirements={true}
                    className="mt-2"
                  />
                </div>
                <div>
                  <label className="font-semibold text-[#172A3A] block mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <button
                onClick={changePassword}
                disabled={changingPassword}
                className="mt-6 w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] px-6 py-3 sm:py-2 font-bold rounded-lg transition disabled:opacity-50"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
            </div>

            {/* Two-Factor Authentication Section */}
            <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="text-blue-600" size={24} />
                <div className="text-xl font-bold text-[#172A3A]">Two-Factor Authentication</div>
              </div>

              {loadingMfa ? (
                <div className="text-gray-500">Loading 2FA status...</div>
              ) : mfaStatus?.mfa?.enabled ? (
                <div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                    <div className="flex items-center gap-2 text-green-700 font-semibold">
                      <CheckCircle2 size={18} />
                      Two-factor authentication is enabled
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Your account is protected with an authenticator app.
                    </p>
                  </div>

                  {mfaStatus.mfa?.enabledAt && (
                    <p className="text-sm text-gray-600 mb-4">
                      Enabled on: {new Date(mfaStatus.mfa.enabledAt).toLocaleDateString()}
                    </p>
                  )}

                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Key size={18} className="text-gray-600" />
                        <span className="font-semibold text-[#172A3A]">Backup Codes</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {mfaStatus.mfa?.backupCodesRemaining !== undefined
                          ? `${mfaStatus.mfa.backupCodesRemaining} backup codes remaining`
                          : 'Backup codes available for account recovery'}
                      </p>
                      <button
                        onClick={() => setShowRegenerateConfirm(true)}
                        className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition"
                      >
                        Generate New Codes
                      </button>
                    </div>

                    <div className="pt-4 border-t">
                      <button
                        onClick={() => setShowDisableConfirm(true)}
                        className="text-red-600 hover:text-red-700 font-medium text-sm"
                      >
                        Disable two-factor authentication
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                    <div className="flex items-center gap-2 text-amber-700 font-semibold">
                      <AlertTriangle size={18} />
                      Two-factor authentication is not enabled
                    </div>
                    <p className="text-sm text-amber-600 mt-1">
                      Add an extra layer of security to protect your account and client data.
                    </p>
                  </div>

                  <div className="mb-6">
                    <h3 className="font-semibold text-[#172A3A] mb-2">Why enable 2FA?</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center gap-2">
                        <Smartphone size={16} className="text-blue-600" />
                        Protects against password theft
                      </li>
                      <li className="flex items-center gap-2">
                        <ShieldCheck size={16} className="text-blue-600" />
                        Required for HIPAA compliance best practices
                      </li>
                      <li className="flex items-center gap-2">
                        <Key size={16} className="text-blue-600" />
                        Backup codes ensure you're never locked out
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={() => setShowMfaSetup(true)}
                    className="w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] font-bold px-6 py-3 rounded-lg transition"
                  >
                    Enable Two-Factor Authentication
                  </button>
                </div>
              )}
            </div>

            {/* Session Security Info */}
            <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="text-blue-600" size={24} />
                <div className="text-xl font-bold text-[#172A3A]">Session Security</div>
              </div>

              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span>Session timeout</span>
                  <span className="font-semibold text-[#172A3A]">15 minutes of inactivity</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span>Concurrent sessions</span>
                  <span className="font-semibold text-[#172A3A]">1 device at a time</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span>Session encryption</span>
                  <span className="font-semibold text-green-600">Enabled (TLS 1.3)</span>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Your session will automatically end after 15 minutes of inactivity for security.
                Logging in from another device will end your current session.
              </p>
            </div>
          </div>
        )}

        {/* Language Preference */}
        {section === "language" && (
          <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl" data-tour="settings-language">
            <div className="text-lg sm:text-xl font-bold text-[#172A3A] mb-4 flex items-center gap-2">
              <Globe size={24} />
              {t('languagePreference')}
            </div>
            <div className="mb-6 text-gray-700">
              {t('chooseLanguage')}
            </div>

            <div className="space-y-4">
              <div
                onClick={() => setSelectedLanguage('en')}
                className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedLanguage === 'en'
                    ? 'border-[#FFB800] bg-amber-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🇺🇸</div>
                    <div>
                      <div className="font-bold text-lg text-[#172A3A]">{t('english')}</div>
                      <div className="text-sm text-gray-600">{t('unitedStates')}</div>
                    </div>
                  </div>
                  {selectedLanguage === 'en' && (
                    <div className="w-8 h-8 rounded-full bg-[#FFB800] flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                  )}
                </div>
              </div>

              <div
                onClick={() => setSelectedLanguage('es')}
                className={`p-6 border-2 rounded-xl cursor-pointer transition-all ${
                  selectedLanguage === 'es'
                    ? 'border-[#FFB800] bg-amber-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🇪🇸</div>
                    <div>
                      <div className="font-bold text-lg text-[#172A3A]">{t('spanish')}</div>
                      <div className="text-sm text-gray-600">Spanish</div>
                    </div>
                  </div>
                  {selectedLanguage === 'es' && (
                    <div className="w-8 h-8 rounded-full bg-[#FFB800] flex items-center justify-center text-white font-bold">
                      ✓
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={saveLanguagePreference}
                className="w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] px-6 py-3 font-bold rounded-lg transition-all shadow-md hover:shadow-lg"
                disabled={selectedLanguage === i18n.language}
              >
                {t('save')} {t('languagePreference')}
              </button>
              {languageSaved && (
                <div className="text-green-600 font-semibold flex items-center gap-2">
                  <Check size={16} /> {t('languageSaved')}
                </div>
              )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="font-semibold text-blue-900 mb-2">📧 {t('autoTranslationNote')}</div>
              <div className="text-sm text-blue-800">
                {t('autoTranslationDesc')}
              </div>
            </div>
          </div>
        )}

        {/* Notifications */}
        {section === "notifications" && (
          <div className="max-w-xl">
            <NotificationSettings />
          </div>
        )}

        {/* Integrations */}
        {section === "integrations" && (
          <IntegrationsSection
            integrations={integrations}
            loadingIntegrations={loadingIntegrations}
            connectIntegration={connectIntegration}
            disconnectIntegration={disconnectIntegration}
          />
        )}

        {/* Branding & Theme */}
        {section === "branding" && (
          <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-2xl" data-tour="settings-branding">
            <div className="text-lg sm:text-xl font-bold text-[#172A3A] mb-4">Branding & Theme</div>

            {!allowCustomBranding && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-center gap-2">
                <AlertTriangle size={16} /> Your agency controls branding settings. Contact your agency owner to make changes.
              </div>
            )}

            {brandingSaved && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <Check size={16} /> Branding settings saved!
              </div>
            )}

            <fieldset disabled={!allowCustomBranding} className="space-y-6">
              {/* Company Name */}
              <div>
                <label className="font-semibold text-[#172A3A] block mb-2">Company/Agent Name</label>
                <input className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800]"
                  value={brandingSettings.companyName}
                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Your Name or Agency Name" />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="font-semibold text-[#172A3A] block mb-2">Logo</label>
                <div className="flex items-start gap-4 flex-wrap">
                  {(logoPreview || brandingSettings.logoUrl) && (
                    <div className="w-20 h-20 border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                      <img src={logoPreview || brandingSettings.logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                    </div>
                  )}
                  <div className="flex-1">
                    <label className="cursor-pointer inline-flex items-center gap-2 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 hover:border-[#FFB800] transition text-sm text-gray-600">
                      <UploadCloud size={18} />
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo (PNG, JPG, SVG — max 5MB)'}
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Upload your logo to extract brand colors automatically.</p>
                  </div>
                </div>

                {/* Extracted Color Swatches */}
                {extractedColors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Extracted colors — click to use:</p>
                    <div className="flex gap-2 flex-wrap">
                      {extractedColors.map((color, i) => (
                        <button key={i} onClick={() => setBrandingSettings(prev => ({ ...prev, primaryColor: color }))}
                          className="w-8 h-8 rounded-full border-2 border-white shadow-md hover:scale-110 transition"
                          style={{ background: color }} title={color} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Primary Color */}
              <div>
                <label className="font-semibold text-[#172A3A] block mb-2">Primary Color</label>
                <div className="flex items-center gap-3 flex-wrap">
                  <input type="color" value={brandingSettings.primaryColor}
                    onChange={(e) => setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-12 h-12 rounded-lg border cursor-pointer" />
                  <input type="text" value={brandingSettings.primaryColor}
                    onChange={(e) => setBrandingSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="px-3 py-2 border rounded-lg font-mono text-sm w-28" placeholder="#FFB800" />
                  <div className="px-4 py-2 rounded-lg text-white font-semibold" style={{ backgroundColor: brandingSettings.primaryColor }}>Preview</div>
                </div>
              </div>

              {/* Live Preview Strip */}
              <div>
                <label className="font-semibold text-[#172A3A] block mb-2">Preview</label>
                <div className="w-40 rounded-xl overflow-hidden shadow-lg" style={{ background: '#172A3A' }}>
                  <div className="p-3 border-b border-gray-700 flex items-center gap-2">
                    {(logoPreview || brandingSettings.logoUrl) ? (
                      <img src={logoPreview || brandingSettings.logoUrl} className="w-6 h-6 rounded bg-white object-contain" alt="" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-gray-600 flex items-center justify-center text-[8px] text-white font-bold">IRP</div>
                    )}
                    <span className="text-xs font-bold" style={{ color: brandingSettings.primaryColor }}>
                      {brandingSettings.companyName || 'Pro IRP'}
                    </span>
                  </div>
                  {['Dashboard','Clients','Settings'].map(item => (
                    <div key={item} className="px-3 py-2 text-xs text-gray-400 hover:text-white">{item}</div>
                  ))}
                </div>
              </div>

              {/* Email Signature */}
              <div>
                <label className="font-semibold text-[#172A3A] block mb-2">Email Signature</label>
                <textarea className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#FFB800] focus:border-[#FFB800] min-h-[120px]"
                  value={brandingSettings.emailSignature}
                  onChange={(e) => setBrandingSettings(prev => ({ ...prev, emailSignature: e.target.value }))}
                  placeholder={"Your Name\nLicensed Medicare Agent\nPhone: (555) 123-4567\nEmail: you@example.com"} />
                <p className="text-xs text-gray-500 mt-1">Added to automated emails.</p>
              </div>
            </fieldset>

            <button onClick={saveBrandingSettings} disabled={savingBranding || !allowCustomBranding}
              className="w-full sm:w-auto bg-[#FFB800] hover:bg-yellow-400 text-[#172A3A] px-6 py-3 sm:py-2 font-bold rounded-lg mt-6 transition disabled:opacity-50 flex items-center justify-center gap-2">
              <Save size={18} />
              {savingBranding ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        )}

        {/* Danger Zone */}
        {section === "danger" && (
          <div className="bg-white rounded-2xl shadow p-4 sm:p-8 max-w-xl border border-red-300">
            <div className="text-lg sm:text-xl font-bold text-red-700 mb-4 flex items-center gap-2">
              <Trash2 size={24} /> {t('dangerZone')}
            </div>
            <div className="mb-4 text-gray-700">
              {t('dangerZoneDesc')}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                type="button"
                className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-6 py-3 sm:py-2 font-bold rounded-lg"
                onClick={async () => {
                  const confirmed = window.confirm(
                    'WARNING: This will permanently delete your account and all associated data. This action CANNOT be undone.\n\nAre you sure you want to delete your account?'
                  );
                  if (!confirmed) return;
                  const doubleConfirm = window.confirm(
                    'Please confirm once more: DELETE your account permanently?'
                  );
                  if (!doubleConfirm) return;
                  try {
                    const res = await fetch(`${API_BASE}/auth/delete-account`, {
                      method: 'DELETE',
                      credentials: 'include',
                    });
                    if (res.ok) {
                      alert('Your account has been deleted. You will be logged out.');
                      window.location.href = '/login';
                    } else {
                      const data = await res.json();
                      alert(data.error || 'Failed to delete account. Please contact support.');
                    }
                  } catch (err) {
                    console.error('Delete account error:', err);
                    alert('An error occurred. Please contact support to delete your account.');
                  }
                }}
              >
                {t('deleteAccount')}
              </button>
              <button
                type="button"
                className="w-full sm:w-auto bg-red-100 hover:bg-red-200 text-red-800 px-4 py-3 sm:py-2 font-bold rounded-lg"
                onClick={() => {
                  alert('To reset all data, please contact support at support@proirp.com. This ensures your data is handled safely.');
                }}
              >
                {t('resetAllData')}
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

    </div>
  );
};

export default Settings;
